<template>
  <div class="container-nzidoo">
    <page-title :title="$t('menu.list.stores')" />

    <!-- Toolbar -->
    <div class="admin-toolbar">
      <div class="admin-search-box">
        <i class="mdi mdi-magnify admin-search-icon"></i>

        <input type="text" v-model="searchTerm" :placeholder="$t('sentence.searchByName')" class="admin-search-input">

        <!-- Clear icon -->
        <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
          :title="$t('sentence.clear')"></i>
      </div>
      <div class="admin-toolbar-filters">
        <select v-model="filterStatus" class="admin-filter-select">
          <option value="all">{{ $t('general.all') || 'Tous' }}</option>
          <option value="validated">{{ $t('status.validated') }}</option>
          <option value="pending">{{ $t('status.notYetValidated') }}</option>
          <option value="refused">{{ $t('status.refused') }}</option>
        </select>
      </div>
    </div>

    <LoadingSpinner v-if="loader" />
    <!-- Stores Grid -->
    <div class="admin-grid">
      <div v-for="store in filteredStores" :key="store.id" class="admin-card">
        <div class="admin-card-header">
          <div class="store-header-info">
            <span class="store-card-name">{{ store.name }}</span>
            <span :class="getStatusClass(store.verified)" class="store-status-badge">
              {{ getStatusText(store.verified) }}
            </span>
          </div>
          <div class="admin-card-actions">
            <button class="btn btn-icon-action btn-primary" @click="showStoreDetails(store.id)"
              :title="$t('action.view')">
              <i class="mdi mdi-eye"></i>
            </button>
          </div>
        </div>
        <div class="admin-card-body">
          <div class="admin-card-section">
            <label class="admin-card-label">{{ $t('attr.phone') }}</label>
            <div class="card-light">
              <i class="mdi mdi-phone"></i>
              {{ formatPhone(store.phone_number) }}
            </div>
          </div>
          <div class="admin-card-section">
            <label class="admin-card-label">{{ $t('attr.address') }}</label>
            <div class="card-light">
              <i class="mdi mdi-map-marker"></i>
              {{ store.address }}
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredStores.length === 0" class="admin-empty-state">
        <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <p class="admin-empty-text">{{ $t('sentence.noResults') || 'Aucun magasin trouvé' }}</p>
      </div>
    </div>

    <!-- Drawer Backdrop -->
    <transition name="drawer-backdrop">
      <div v-if="showModal" class="admin-drawer-backdrop" @click="closeModal"></div>
    </transition>

    <!-- Store Details Drawer -->
    <transition name="drawer">
      <div v-if="showModal && selectedStore" class="admin-drawer-container store-drawer-wide">
        <div class="admin-drawer-header">
          <div>
            <h3 class="admin-drawer-title">{{ selectedStore.name }}</h3>
            <p class="admin-drawer-subtitle">{{ $t('ecommerce.shop-info') }}</p>
          </div>
          <button class="btn btn-close" @click="closeModal">
            <i class="mdi mdi-close"></i>
          </button>
        </div>

        <div class="admin-drawer-body">
          <!-- Alert Message -->
          <div v-if="message" class="store-alert">
            <i class="mdi mdi-alert-circle"></i>
            {{ message }}
          </div>

          <!-- User Profile Section -->
          <div class="store-profile-section">
            <div class="store-avatar">
              <img :src="getUserPicture(selectedStore.user.picture)" alt="user image" />
            </div>
            <div class="store-profile-info">
              <h4>{{ selectedStore.user.email }}</h4>
              <span :class="getStatusClass(selectedStore.verified)" class="store-status-badge-large">
                {{ getStatusText(selectedStore.verified) }}
              </span>
            </div>
          </div>

          <!-- Action Buttons -->
          <div v-if="selectedStore.verified === null" class="store-action-buttons">
            <button @click="respondStore(selectedStore, 'accept')" class="btn btn-success btn-lg">
              <i class="mdi mdi-check"></i>
              {{ $t('action.accept') }}
            </button>
            <button @click="respondStore(selectedStore, 'refuse')" class="btn btn-danger btn-lg">
              <i class="mdi mdi-close"></i>
              {{ $t('action.refuse') }}
            </button>
          </div>

          <!-- Information Grid -->
          <div class="store-info-grid">
            <!-- Profile Column -->
            <div class="store-info-column">
              <h5 class="store-section-title">{{ $t('attr.profile') }}</h5>

              <div class="store-info-item">
                <label>{{ $t('attr.email') }}</label>
                <p>{{ selectedStore.user.email }}</p>
              </div>

              <div class="store-info-item">
                <label>{{ $t('attr.phone') }}</label>
                <p>{{ selectedStore.user.phone }}</p>
              </div>

              <div class="store-info-item">
                <label>{{ $t('attr.birthday') }}</label>
                <p>{{ selectedStore.user.birthday || 'N/A' }}</p>
              </div>

              <div class="store-info-item">
                <label>{{ $t('attr.address') }}</label>
                <p>
                  {{ selectedStore.user.address }}
                  {{ selectedStore.user.postal_code }}
                  {{ selectedStore.user.city }}
                  {{ selectedStore.user.country }}
                </p>
              </div>
            </div>

            <!-- Client Info Column -->
            <div class="store-info-column">
              <h5 class="store-section-title">{{ $t('sentence.clientInfo') }}</h5>

              <div class="store-info-item">
                <label>{{ $t('ecommerce.cardId') }}</label>
                <a v-if="selectedStore.user.card_id" :href="config.storageUrl + selectedStore.user.card_id"
                  class="btn btn-primary btn-sm" target="_blank">
                  <i class="mdi mdi-file-document"></i>
                  {{ $t('action.view') }}
                </a>
                <p v-else class="store-no-file">{{ $t('sentence.noFileUploadedYet') }}</p>
              </div>

              <h5 class="store-section-title" style="margin-top: 24px;">{{ $t('sentence.clientPro') }}</h5>

              <div class="store-info-item">
                <label>{{ $t('ecommerce.rc') }}</label>
                <a v-if="selectedStore.user.rc" :href="config.storageUrl + selectedStore.user.rc"
                  class="btn btn-primary btn-sm" target="_blank">
                  <i class="mdi mdi-file-document"></i>
                  {{ $t('action.view') }}
                </a>
                <p v-else class="store-no-file">{{ $t('sentence.noFileUploadedYet') }}</p>
              </div>
            </div>

            <!-- Shop Info Column -->
            <div class="store-info-column">
              <h5 class="store-section-title">{{ $t('ecommerce.shop-info') }}</h5>

              <div class="store-info-item">
                <label>{{ $t('ecommerce.store-name') }}</label>
                <p>{{ selectedStore.name }}</p>
              </div>

              <div class="store-info-item">
                <label>{{ $t('attr.address') }}</label>
                <p>{{ selectedStore.address }}</p>
              </div>

              <div class="store-info-item">
                <label>{{ $t('attr.phone') }}</label>
                <p>{{ formatPhone(selectedStore.phone_number) }}</p>
              </div>

              <div class="store-info-item">
                <label>{{ $t('attr.logo') }}</label>
                <div v-if="selectedStore.picture" class="store-logo-preview">
                  <img :src="config.storageUrl + selectedStore.picture" alt="Store logo" />
                </div>
                <p v-else class="store-no-file">{{ $t('sentence.noFileUploadedYet') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <notifications style="top:5%;" width="35%" group="error" position="top center" />
    <notifications style="top:5%;" width="35%" group="success" position="top center" />
  </div>
</template>

<script>
import PageTitle from '@/components/templates/PageTitle.vue';
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";
import config from "@/config";
import userpng from '@/assets/img/user/user.webp';

export default {
  components: { PageTitle,LoadingSpinner },
  name: "StoresAdmin",

  data() {
    return {
      userpng: userpng,
      config: config,
      stores: [],
      selectedStore: null,
      showModal: false,
      searchTerm: "",
      filterStatus: "all",
      loader: false,
      message: ""
    };
  },

  computed: {
    filteredStores() {
      let filtered=this.stores;

      // Filter by search term
      if (this.searchTerm) {
        const term=this.searchTerm.toLowerCase();
        filtered=filtered.filter(store =>
          store.name.toLowerCase().includes(term)||
          store.address.toLowerCase().includes(term)||
          (store.phone_number&&store.phone_number.toString().includes(term))
        );
      }

      // Filter by status
      if (this.filterStatus!=='all') {
        filtered=filtered.filter(store => {
          if (this.filterStatus==='validated') return store.verified===1;
          if (this.filterStatus==='pending') return store.verified===null;
          if (this.filterStatus==='refused') return store.verified===0;
          return true;
        });
      }

      return filtered;
    }
  },

  mounted() {
    this.loadStores();
  },

  methods: {
    clearSearch() {
      this.searchTerm="";
    },
    async loadStores() {
      try {
        this.loader=true;
        const res=await this.$axios.get("/store");
        this.stores=res.data;
        this.loader=false;
      } catch (err) {
        console.error("Error loading stores:", err);
        this.$notify({
          group: "error",
          title: this.$t('sentence.errorLoading')||"Erreur lors du chargement",
          type: "error",
        });
      }
    },

    async showStoreDetails(id) {
      try {
        const res=await this.$axios.get(`/store/${id}`);
        this.selectedStore=res.data[0];
        this.showModal=true;
        this.message="";
      } catch (err) {
        console.error("Error loading store:", err);
        this.$notify({
          group: "error",
          title: this.$t('sentence.errorLoading')||"Erreur lors du chargement",
          type: "error",
        });
      }
    },

    closeModal() {
      this.showModal=false;
      this.selectedStore=null;
      this.message="";
    },

    async respondStore(selectedStore, action) {
      this.message="";

      if (action==="accept"&&selectedStore.user.rc===null) {
        this.message="Le magasin ne possède pas de registre de commerce.";
        return;
      }

      try {
        await this.$axios.post("/admin/store/respondTo", {
          id: selectedStore.id,
          action: action
        });

        this.$notify({
          group: "success",
          title: action==='accept'
            ? this.$t('status.validated')||"Magasin validé"
            :this.$t('status.refused')||"Magasin refusé",
          type: "success",
        });

        this.closeModal();
        this.loadStores();
      } catch (err) {
        console.error("Error responding to store:", err);
        this.$notify({
          group: "error",
          title: this.$t('sentence.errorSaving')||"Erreur lors de l'opération",
          type: "error",
        });
      }
    },

    getStatusClass(verified) {
      if (verified===1) return 'status-validated';
      if (verified===null) return 'status-pending';
      return 'status-refused';
    },

    getStatusText(verified) {
      if (verified===1) return this.$t('status.validated');
      if (verified===null) return this.$t('status.notYetValidated');
      return this.$t('status.refused');
    },

    getUserPicture(picture) {
      return picture? this.config.storageUrl+picture:this.userpng;
    },

    formatPhone(phone) {
      return phone? `0${phone}`:'N/A';
    }
  }
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>

/* Store-specific styles */
.store-header-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.store-card-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--gray-900);
}

.store-status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: fit-content;
}

.status-validated {
  background: #d4edda;
  color: #155724;
}

.status-pending {
  background: #fff3cd;
  color: #856404;
}

.status-refused {
  background: #f8d7da;
  color: #721c24;
}

.admin-toolbar-filters {
  display: flex;
  gap: 12px;
}

.admin-filter-select {
  padding: 10px 16px;
  border: 2px solid var(--gray-200);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  background: white;
  color: var(--gray-700);
  cursor: pointer;
  transition: all 0.2s ease;
}

.admin-filter-select:focus {
  outline: none;
  border-color: var(--color-nz);
  box-shadow: 0 0 0 3px rgba(38, 149, 255, 0.1);
}

/* Wider drawer for store details */
.store-drawer-wide {
  max-width: 900px;
}

.store-alert {
  padding: 16px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 10px;
  color: #856404;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
}

.store-alert i {
  font-size: 24px;
}

.store-profile-section {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: var(--gray-50);
  border-radius: 12px;
  margin-bottom: 24px;
}

.store-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.store-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.store-profile-info {
  flex: 1;
}

.store-profile-info h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--gray-900);
}

.store-status-badge-large {
  display: inline-block;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.store-action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.store-action-buttons .btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
}

.store-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
}

.store-info-column {
  background: white;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--gray-200);
}

.store-section-title {
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--gray-900);
  padding-bottom: 12px;
  border-bottom: 2px solid var(--gray-200);
}

.store-info-item {
  margin-bottom: 20px;
}

.store-info-item:last-child {
  margin-bottom: 0;
}

.store-info-item label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--gray-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.store-info-item p {
  margin: 0;
  font-size: 14px;
  color: var(--gray-700);
  font-weight: 500;
}

.store-no-file {
  color: var(--gray-400) !important;
  font-style: italic;
}

.store-logo-preview {
  width: 100%;
  max-width: 200px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid var(--gray-200);
  background: white;
}

.store-logo-preview img {
  width: 100%;
  height: auto;
  display: block;
}

@media (max-width: 768px) {
  .store-drawer-wide {
    max-width: 100%;
  }

  .store-info-grid {
    grid-template-columns: 1fr;
  }

  .store-profile-section {
    flex-direction: column;
    text-align: center;
  }

  .admin-toolbar-filters {
    width: 100%;
  }

  .admin-filter-select {
    width: 100%;
  }
}
</style>